/**
 * 危险参数判定。
 *
 * 为什么需要它：`PARAM_SET` 是"发出去就生效"的，改掉 `ARMING_` / `FS_` / `FENCE_`
 * 这类参数等于改变**飞行行为本身** —— 写错一个值，下一次解锁、或失控保护触发时的表现
 * 会完全不同。而参数表有 1600 行，搜索之后很容易改到相邻的那一条。
 *
 * 判定刻意**保守且可解释**：只列"改动会直接改变机体行为"的命名空间，而不是看起来
 * 专业的一大片前缀。把 `INS_`（绝大多数是安装偏移）、`BATT_CAPACITY` 这类也算危险，
 * 结果是每三次写入弹两次确认 —— 确认框随即变成被无脑点掉的噪音，真正危险的那次也一起被点掉。
 */

export interface DangerousParamRule {
  readonly id: string;
  readonly match: (name: string) => boolean;
  /** 为什么危险（弹窗里的后果文案由调用点补上具体参数名与值） */
  readonly reason: string;
}

function prefix(value: string): (name: string) => boolean {
  return (name) => name.startsWith(value);
}

/** 顺序不影响判定（取第一条命中的规则），但按"越具体越靠前"排列便于阅读 */
export const DANGEROUS_PARAM_RULES: readonly DangerousParamRule[] = Object.freeze([
  {
    id: 'arming',
    match: prefix('ARMING_'),
    reason: '解锁检查：改错会让该被拦住的解锁通过，或让正常的解锁被拒绝',
  },
  {
    id: 'failsafe',
    match: prefix('FS_'),
    reason: '失控保护：改错会让保护在该触发时没触发（或飞行中误触发）',
  },
  {
    id: 'geofence',
    match: prefix('FENCE_'),
    reason: '地理围栏：改错会让围栏失效或把飞机挡在允许区域之外',
  },
  {
    id: 'flight-mode',
    match: prefix('FLTMODE'),
    reason: '飞行模式开关映射：改错会让某个开关位置切到意料之外的模式',
  },
  {
    id: 'motor',
    match: prefix('MOT_'),
    reason: '电机输出：改错会影响解锁后的电机行为',
  },
  {
    id: 'servo-function',
    match: (name) => name.startsWith('SERVO') && name.endsWith('_FUNCTION'),
    reason: '输出通道功能映射：改错会让某个通道去驱动另一个执行机构',
  },
  {
    id: 'frame',
    match: (name) => name === 'FRAME_CLASS' || name === 'FRAME_TYPE',
    reason: '机架类型：与实物不符的机架类型会让姿态控制在解锁后立刻发散',
  },
  {
    id: 'rtl',
    match: (name) => name === 'RTL_ALT' || name === 'RTL_ALT_FINAL',
    reason: '返航高度：改错会让返航在过低或过高的高度上执行',
  },
  {
    id: 'battery-threshold',
    match: (name) => name === 'BATT_LOW_VOLT' || name === 'BATT_CRT_VOLT',
    reason: '电池保护阈值：改错会让低电量保护在该触发时没有触发',
  },
  {
    id: 'tuning',
    match: prefix('ATC_'),
    reason: '姿态控制增益：改错会引起振荡或让姿态响应变迟钝',
  },
  {
    id: 'ekf',
    match: prefix('EK3_'),
    reason: '状态估计（EKF3）：改错会让位置/姿态估计失效',
  },
]);

const CACHE = new Map<string, DangerousParamRule | null>();

export function dangerousRuleOf(name: string): DangerousParamRule | null {
  const cached = CACHE.get(name);
  if (cached !== undefined) return cached;
  const found = DANGEROUS_PARAM_RULES.find((rule) => rule.match(name)) ?? null;
  CACHE.set(name, found);
  return found;
}

export function isDangerousParam(name: string): boolean {
  return dangerousRuleOf(name) !== null;
}

export function dangerReasonOf(name: string): string | null {
  return dangerousRuleOf(name)?.reason ?? null;
}
